import React, {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import {Form, Formik} from "formik";
import debounce from "lodash.debounce";
import * as yup from "yup";
//local
import {Modal} from "components/Modal";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import ButtonDelete from "components/buttons/ButtonDelete";
import MiniSelect from "components/selects/MiniSelect";
import {Preloader} from "components/Preloader";
import {Input} from "components/Input";
import FormikField from "components/FormikField";
import {CustomTooltip} from "components/CustomTooltip";
import {ConfirmModal} from "components/ConfirmModal";
import {IconChevronLeft, IconPlus, IconPapers} from "components/icons";
import {
  getInvoiceProfiles,
  createInvoiceProfile,
  getInvoiceProfile,
  updateInvoiceProfile,
  deleteInvoicingProfile,
} from "../api/contracts";
import {SignContractProfileField} from "./atoms/SignContractProfileField";
import {SignContractOurProfileHead} from "./atoms/SignContractOurProfileHead";
import {SignContractOurProfileRow} from "./atoms/SignContractOurProfileRow";
import {color} from "constants/color.consts";
import {emailNullable} from "yup/defaultValudations";
import {listLanguageKey, itemLanguageKey, invoiceProfileFieldProps} from "../constants";
import "../styles/modal-our-profiles.scss";

// source: settings, signContract

export const OurProfilesModal = inject("store")(
  observer(({store: {nsi, clientStore}, isOwner, onChangeProfile, onClose}) => {
    const {language} = clientStore;

    const {t} = useTranslation();

    const searchInputRef = useRef(null);
    const listRef = useRef(null);

    const [invoiceProfiles, setInvoiceProfiles] = useState(null);
    const [countries, setCountries] = useState(null);
    const [editProfileId, setEditProfileId] = useState(null);
    const [editProfile, setEditProfile] = useState({});
    const [searchField, setSearchField] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);

    const loading = useMemo(() => !invoiceProfiles, [invoiceProfiles]);
    const isNewProfile = useMemo(() => editProfileId === "new", [editProfileId]);
    const hasProfile = useMemo(
      () => isNewProfile || (editProfileId && editProfile?.id),
      [isNewProfile, editProfile, editProfileId]
    );
    const hasUnsavedChanges = (values) =>
      JSON.stringify(values) !== JSON.stringify(editProfile);
    const isListProfiles = useMemo(
      () => !!invoiceProfiles?.invoiceProfiles?.length,
      [invoiceProfiles]
    );

    const getProfiles = useCallback(
      async (page) =>
        await getInvoiceProfiles({name: searchField, page: page ?? 1, size: 50}).then(
          (data) =>
            setInvoiceProfiles((prev) =>
              page
                ? {
                    ...data,
                    invoiceProfiles: [...prev.invoiceProfiles, ...data.invoiceProfiles],
                  }
                : data
            )
        ),
      [searchField]
    );

    const getProfile = useCallback(async (id) => {
      const profile = await getInvoiceProfile(id);
      setEditProfile(profile);
      return profile;
    }, []);

    const addNewProfile = () => {
      setEditProfileId("new");
      setEditProfile({name: "", companyAddressCountry: "752", addressCountry: "752"});
    };

    const clearProfile = () => {
      setEditProfileId(null);
      setEditProfile(null);
    };

    const handleRemoveProfile = () =>
      ConfirmModal({
        title: t("SMART_TEMPLATES_MODAL_TEMPLATE_WANT_DELETE"),
        text: t("SMART_TEMPLATES_MODAL_TEMPLATE_CANT_RETURN"),
        type: "warning",
        onOk: removeProfile,
      });

    const removeProfile = async () => {
      await deleteInvoicingProfile(editProfileId);
      clearProfile();
      await getProfiles();
    };

    const handleSaveProfile = async (values) => {
      const payload = {...values};
      setSaveLoading(true);
      isNewProfile
        ? await createInvoiceProfile(payload)
        : await updateInvoiceProfile(editProfileId, payload);
      clearProfile();
      setSaveLoading(false);
      await getProfiles();
    };

    const handleSelectProfile = (id) =>
      getProfile(id).then((profile) => {
        onChangeProfile(profile);
        onClose();
      });

    const searchOnChange = debounce((e) => {
      const name = e?.target?.value?.trim();
      if (name?.length) setSearchField(name.replace(/^(\d{6})-(\d{4})$/, "$1$2"));
      else setSearchField(null);
    }, 400);

    const schema = yup.object().shape({
      email: emailNullable(),
    });

    const loadNextPageData = async () => {
      if (!(invoiceProfiles?.totalPages - 1 >= invoiceProfiles?.currentPage)) return;
      const nextPage = ++invoiceProfiles.currentPage;
      await getProfiles(nextPage);
      listRef.current.addEventListener("scroll", handleScroll);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleScroll = () => {
      const scrollPosition = listRef.current.scrollTop + listRef.current.clientHeight;
      const maxScrollPosition = listRef.current.scrollHeight - 150;
      if (listRef.current && scrollPosition >= maxScrollPosition) {
        listRef.current.removeEventListener("scroll", handleScroll);
        loadNextPageData();
      }
    };

    useEffect(() => {
      const container = listRef.current;
      if (container) {
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
      }
    }, [handleScroll]);

    useEffect(() => {
      getProfiles();
    }, [getProfiles]);

    useEffect(() => {
      nsi.getCountries(language).then(setCountries);
    }, [nsi, language]);

    useEffect(() => {
      editProfileId && !isNewProfile && getProfile(editProfileId);
    }, [editProfileId, getProfile, isNewProfile]);

    return (
      <Modal
        onClose={onClose}
        width={820}
        boldTitle
        tightTitle
        noCenterContent
        titleIcon={<IconPapers addClass="mr-4" />}
        titleText="SIGN_CONTRACT_INVOICING_PROFILES_MODAL_TITLE"
        addClass="modal-our-profiles_wrapper"
        contentClassName="modal-our-profiles"
      >
        <div>
          {loading && <Preloader addClass="preloader250" />}
          {!loading && (
            <React.Fragment>
              {hasProfile ? (
                <div className="modal-our-profiles__content pt-4">
                  <ButtonTransparent
                    icon={<IconChevronLeft color={color.blue} />}
                    noBorder
                    uppercase
                    bold
                    small
                    addClass="pl-0"
                    name={t(`${itemLanguageKey}BACK_BUTTON`)}
                    onClick={clearProfile}
                  />
                  <Formik
                    initialValues={editProfile}
                    validationSchema={schema}
                    enableReinitialize
                    onSubmit={handleSaveProfile}
                  >
                    {({values, setFieldValue}) => (
                      <Form>
                        <div className="df-row-center mt-4">
                          <FormikField
                            name="name"
                            small
                            boldText
                            width="350px"
                            height="40px"
                            placeholder={t(`${itemLanguageKey}PROFILE_NAME_PLACEHOLDER`)}
                            className="input mb-0 fz-20"
                            addClass="mr-2 mb-0"
                          />
                          <ButtonDelete onClick={handleRemoveProfile} activeOnHover />
                          <CustomTooltip
                            text={onChangeProfile && `${itemLanguageKey}BUTTON_TOOLTIP`}
                            disabled={values?.name && hasUnsavedChanges(values)}
                            centerText
                          >
                            <div className="pa-0 df mla">
                              <ButtonTransparent
                                name="SAVE_CHANGES"
                                medium
                                nonTransparent
                                disabled={!(values.name && hasUnsavedChanges(values))}
                                loading={saveLoading}
                                type="submit"
                              />
                            </div>
                          </CustomTooltip>
                        </div>
                        <div className="modal-our-profiles__profile-body">
                          <div className="modal-our-profiles__profile-body-side ml-4">
                            <div className="df-row-center">
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.companyName}
                                addClass="mr-2 mb-2 fg2"
                              />
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.companyNumber}
                              />
                            </div>
                            <SignContractProfileField
                              {...invoiceProfileFieldProps.companyAddressMain}
                              addClass="mr-16 mb-1"
                            />
                            <SignContractProfileField
                              {...invoiceProfileFieldProps.companyAddressExtra}
                              addClass="mr-16 mb-1"
                            />
                            <div className="df-row-center">
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.companyAddressPostalCode}
                                addClass="mr-2 mb-1"
                              />
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.companyAddressCity}
                                addClass="mr-16 mb-1"
                              />
                            </div>
                            <MiniSelect
                              list={countries}
                              defaultValue={values.companyAddressCountry}
                              itemAsObject
                              addDropdownClass="wdth-170"
                              addClass="mb-4"
                              onChange={(value) =>
                                setFieldValue("companyAddressCountry", value)
                              }
                            />
                            <SignContractProfileField
                              {...invoiceProfileFieldProps.intDesignation}
                            />
                          </div>
                          <div className="modal-our-profiles__profile-body-side">
                            <SignContractProfileField
                              {...invoiceProfileFieldProps.email}
                            />
                            <SignContractProfileField
                              {...invoiceProfileFieldProps.addressMain}
                              addClass="mr-16 mb-1"
                            />
                            <SignContractProfileField
                              {...invoiceProfileFieldProps.addressExtra}
                              addClass="mr-16 mb-1"
                            />
                            <div className="df-row-center">
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.addressPostalCode}
                                addClass="mr-2 mb-1"
                              />
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.addressCity}
                                addClass="mr-16 mb-1"
                              />
                            </div>
                            <MiniSelect
                              list={countries}
                              defaultValue={values.addressCountry}
                              itemAsObject
                              addDropdownClass="wdth-170"
                              addClass="mb-4"
                              onChange={(value) => setFieldValue("addressCountry", value)}
                            />
                            <div className="df-row-center">
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.glnNumber}
                                addClass="mr-2 mb-2 fg2"
                              />
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.peppolId}
                              />
                            </div>
                            <div className="df-row-center">
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.inxNumber}
                                addClass="mr-2 mb-2 fg2"
                              />
                              <SignContractProfileField
                                {...invoiceProfileFieldProps.vatNumber}
                              />
                            </div>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              ) : (
                <div className="modal-our-profiles__content pt-4">
                  <Input
                    small
                    fullWidth
                    focus={searchInputRef}
                    autoFocus
                    defaultValue={searchField}
                    onChange={searchOnChange}
                    placeholder={t(`${listLanguageKey}INPUT_SEARCH_PLACEHOLDER`)}
                    showClearIcon
                    onClear={() => searchOnChange()}
                  />
                  {isListProfiles ? (
                    <React.Fragment>
                      <SignContractOurProfileHead isOwner={isOwner} t={t} />
                      <div className="modal-our-profiles__table-body" ref={listRef}>
                        {invoiceProfiles.invoiceProfiles?.map((profile) => (
                          <SignContractOurProfileRow
                            key={profile.id}
                            profile={profile}
                            isOwner={isOwner}
                            onSelect={onChangeProfile ? handleSelectProfile : undefined}
                            onEdit={setEditProfileId}
                          />
                        ))}
                      </div>
                    </React.Fragment>
                  ) : (
                    <div className="modal-our-profiles__table-body mt-4">
                      <div className="null-state-wrapper pa-3">
                        <div className="mb-2">{t(`${listLanguageKey}NO_DATA`)}</div>
                      </div>
                    </div>
                  )}

                  {isOwner && (
                    <div className="df-row-jce-center mt-4">
                      <ButtonTransparent
                        name="SIGN_CONTRACT_INVOICING_PROFILES_MODAL_NEW_BUTTON"
                        small
                        icon={<IconPlus />}
                        onClick={addNewProfile}
                      />
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </Modal>
    );
  })
);
